import { css } from "@emotion/css";
import { XmarkIcon, type IconProps } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import { useRef, useState } from "react";
import { useObserveElementSize } from "../../hooks/useObserveElementSize";
import { resetStyles } from "../../utils";
import { type AvatarProps } from "../Avatar/Avatar";
import { Tooltip } from "../Tooltip/Tooltip";
export interface TagProps {
    /**
     * The text content to display in the tag.
     */
    label: string;
    /**
     * Optional icon or avatar to display before the label.
     * Can be either an Icon component, or an Avatar component for an entity.
     */
    prefix?: React.ReactElement<IconProps> | React.ReactElement<AvatarProps>;
    /**
     * Callback function invoked when the dismiss button is clicked.
     * When provided, a dismiss button (X) will be displayed on the tag.
     */
    onDismiss?: () => void;
}
export function Tag({ label, prefix, onDismiss }: TagProps) {
    const [tagElement, setTagElement] = useState<HTMLDivElement | null>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const [enableTooltip, setEnableTooltip] = useState(false);
    useObserveElementSize(tagElement, () => {
        if (!labelRef.current)
            return;
        const { clientWidth, scrollWidth } = labelRef.current;
        setEnableTooltip(clientWidth < scrollWidth);
    });
    const dismissButton = onDismiss ? (<button className={styles.dismissButton} onClick={onDismiss} aria-label={`Remove ${label}`}>
            <XmarkIcon size={20}/>
        </button>) : undefined;
    const tag = (<div ref={setTagElement} className={styles.root}>
            <div className={styles.labelContainer}>
                {prefix && <span className={styles.prefix}>{prefix}</span>}
                <span ref={labelRef} className={styles.label}>
                    {label}
                </span>
            </div>
            {dismissButton}
        </div>);
    return enableTooltip ? <Tooltip content={label}>{tag}</Tooltip> : tag;
}
const styles = {
    root: css({
        boxSizing: "border-box",
        display: "inline-flex",
        maxWidth: 180,
        borderRadius: borderRadius.small,
        background: themeTokens.color.tag.background.default,
        overflow: "clip",
    }),
    labelContainer: css({
        display: "inline-flex",
        minWidth: 0,
        alignItems: "center",
        gap: space[6],
        padding: `${space[4]} ${space[6]}`,
    }),
    prefix: css({
        color: themeTokens.color.icon.primary,
    }),
    label: css({
        flex: 1,
        minWidth: 0,
        color: themeTokens.color.text.primary,
        overflow: "clip",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        font: text.body.regular.medium,
    }),
    dismissButton: css({
        ...resetStyles.button,
        padding: `${space[4]} ${space[6]}`,
        color: themeTokens.color.icon.link.default,
        transitionTimingFunction: "ease-in-out",
        transitionProperty: "background-color, color",
        transitionDuration: "0.2s",
        ":hover": {
            backgroundColor: themeTokens.color.tag.background.hover,
            color: themeTokens.color.icon.link.hover,
        },
        ":active": {
            backgroundColor: themeTokens.color.tag.background.hover,
            color: themeTokens.color.icon.link.hover,
        },
        ":focus-visible": {
            outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
            outlineOffset: `-${borderWidth[2]}`,
        },
    }),
};
